import { useState } from 'react';
import { useRecruitmentApi } from 'infrastructure/api';
import { useCandidateId } from 'domain/recruiting/candidate/useCandidateId';
import { CANDIDATE_RESOURCE_URL, CANDIDATE_TIMELINE_OFFER_STATUSES } from 'domain/shared/candidate';
import { useMessageService, ErrorMessage } from 'framework/ui/Message';
import { useBaseCrudService } from 'framework/crud';
import { offerValidationSchema } from 'domain/recruiting/candidate/offercompensation/createupdate/candidateOfferValidation';
import { useRecruitingFinanceService, BatchSensitiveRecruitingInfo } from 'domain/shared/recruitingFinance';
import { useVacancyGroupService } from 'domain/shared/newVacancy';
import { useCandidateService } from 'domain/shared/candidate';
import useDocumentStorageService from 'domain/shared/documentStorage/documentStorageService';
import { CURRENCY_TYPES } from 'domain/shared/core/constants';
import { EMPLOYEE_CONTRACT_TYPES } from 'domain/shared/employee';
import { POSITION_SLOT_API_OFFICE_LABELS } from 'domain/shared/employee/office';
import { uploadOfferPdfFile } from './offerDocument';
export const RECRUITING_OFFER_READ_CALCULATED_AMOUNT_GROSS = 'RECRUITING_OFFER_READ_CALCULATED_AMOUNT_GROSS';

const OFFER_URL = '/offers';

interface CompensationDetail {
  baseAmountType: 'MONTHLY' | 'HOURLY' | 'FIXED';
  currency: keyof typeof CURRENCY_TYPES;
  finalAmountGross?: number;
  probationAmountGross?: number;
  calculatedAmountGross: number;
  probationPeriodReduction?: number;
  office: keyof typeof POSITION_SLOT_API_OFFICE_LABELS;
  contractType: keyof typeof EMPLOYEE_CONTRACT_TYPES;
}

const fieldsToEncrypt = [
  'finalAmountGross',
  'probationAmountGross',
  'probationPeriodReduction',
  'calculatedAmountGross',
] as const;

type SensitiveFieldName = (typeof fieldsToEncrypt)[number];
type CompensationDetailIdField = `${SensitiveFieldName}Id`;

type CompensationDetailWithIds = CompensationDetail & Partial<Record<CompensationDetailIdField, number | string>>;
type FormCompensationDetail = CompensationDetail & Partial<Record<SensitiveFieldName, number>>;

interface OfferData {
  id?: string;
  candidateId?: string;
  status?: (typeof CANDIDATE_TIMELINE_OFFER_STATUSES)[keyof typeof CANDIDATE_TIMELINE_OFFER_STATUSES];
  compensationDetails: CompensationDetailWithIds[];
  offerDocumentPath?: Nullable<string>;
}

type ApiErrorWithJson = {
  json?: () => Promise<{ message?: string }>;
};
type Dictionary<T> = { [key: string]: T };
type FormFieldsData = Dictionary<any>;

const getApiErrorMessage = async (error: unknown, fallbackMessage: string) => {
  const apiError = error as ApiErrorWithJson;
  const errorJson = await apiError.json?.().catch(() => null);

  return errorJson?.message || fallbackMessage;
};

const useCandidateOfferService = () => {
  const [offerData, setOfferData] = useState<OfferData | null>(null);
  const { sendPostRequest, sendPutRequest, sendDeleteRequest, sendGetRequest } = useRecruitmentApi();
  const { saveBatchSensitiveRecruitingInfo } = useRecruitingFinanceService();
  const { retrieveSensitiveRecruitingInfo } = useVacancyGroupService();
  const { getCompleteCandidate } = useCandidateService();
  const { uploadArtifactRaw } = useDocumentStorageService();
  const candidateId = useCandidateId();
  const parentResourceUrl = `${CANDIDATE_RESOURCE_URL}`;

  const { addMessage } = useMessageService();

  const {
    getById: initialGetById,
    update: initialUpdate,
    deleteById,
    ...baseCrudRequests
  } = useBaseCrudService({
    singleResourceUrl: `${parentResourceUrl}${OFFER_URL}/:id`,
    listResourceUrl: `${parentResourceUrl}${OFFER_URL}`,
    useService: useRecruitmentApi,
  });

  const getById = async (offerId: any) => {
    try {
      const offerResponse = await initialGetById(offerId);

      if (!offerResponse) {
        return null;
      }

      const allIds: string[] = offerResponse.compensationDetails
        .flatMap((details: any) => fieldsToEncrypt.map(fieldName => details[`${fieldName}Id`]))
        .filter(Boolean);

      let sensitiveInfoMap;
      try {
        sensitiveInfoMap = await retrieveSensitiveRecruitingInfo(allIds);
      } catch (error) {
        sensitiveInfoMap = new Map();
      }

      const updatedOfferResponse = {
        ...offerResponse,
        compensationDetails: offerResponse.compensationDetails.map((compensation: any) => {
          const updatedCompensation = { ...compensation };

          fieldsToEncrypt.forEach(fieldName => {
            const id = compensation[`${fieldName}Id`];
            updatedCompensation[fieldName] = sensitiveInfoMap.get(id) || '';
            // delete updatedCompensation[idField];
          });

          return updatedCompensation;
        }),
      };

      setOfferData(updatedOfferResponse);
      return updatedOfferResponse;
    } catch (error) {
      addMessage(new ErrorMessage('Error in getting offer'));
      throw error;
    }
  };

  const prepareCompensationDetails = async (
    formCompensationDetails: FormCompensationDetail[],
    currentData?: OfferData | null,
  ) =>
    Promise.all(
      formCompensationDetails.map(async (details, index: number) => {
        const oldCompensationDetails = currentData?.compensationDetails[index];
        const sensitiveData = fieldsToEncrypt
          .map(fieldName => {
            const newValue = details[fieldName];
            const oldValue = oldCompensationDetails?.[fieldName];
            const idFieldName: CompensationDetailIdField = `${fieldName}Id`;

            if (!newValue) return false;

            if (newValue === oldValue)
              return {
                externalId: fieldName,
                id: oldCompensationDetails?.[idFieldName],
                skipEncryption: true,
              };

            return {
              value: newValue,
              externalId: fieldName,
            };
          })
          .filter(item => item && !item.skipEncryption);

        let encryptedValues: Record<string, number | string | undefined> = {};

        if (sensitiveData.length > 0) {
          const savedSensitiveData = await saveBatchSensitiveRecruitingInfo(
            sensitiveData as BatchSensitiveRecruitingInfo[],
          );

          encryptedValues = Object.fromEntries(
            savedSensitiveData.map(savedData => [`${savedData.externalId}Id`, savedData.id]),
          );
        }

        fieldsToEncrypt.forEach(fieldName => {
          const idFieldName: CompensationDetailIdField = `${fieldName}Id`;

          if (!encryptedValues[`${fieldName}Id`]) {
            encryptedValues[idFieldName] = details[fieldName] ? oldCompensationDetails?.[idFieldName] : undefined;
          }
        });

        return {
          baseAmountType: details.baseAmountType,
          currency: details.currency,
          office: details.office,
          contractType: details.contractType,
          ...encryptedValues,
        };
      }),
    );

  const uploadOfferDocument = async (offer: OfferData) => {
    if (!candidateId || !offer.id) return null;

    try {
      const candidate = await getCompleteCandidate(candidateId);
      const { tempPath } = await uploadOfferPdfFile({
        offer,
        candidate,
        uploadFile: uploadArtifactRaw,
      });
      const response = await sendPostRequest(
        `${parentResourceUrl}${OFFER_URL}/${candidateId}/${offer.id}/document/commit`,
        {
          paths: [tempPath],
        },
      );
      const committedOffer = await response.json();

      return committedOffer?.offerDocumentPath ?? null;
    } catch (error: unknown) {
      const errorMessage = await getApiErrorMessage(error, 'Failed to save offer document');

      addMessage(new ErrorMessage(errorMessage));
      throw new Error(errorMessage);
    }
  };

  const createOffer = async (formData: FormFieldsData) => {
    try {
      const compensationDetails = await Promise.all(
        formData.compensationDetails.map(async (details: any) => {
          const sensitiveData = fieldsToEncrypt
            .map(fieldName => {
              if (!details[fieldName]) {
                return false;
              }
              return {
                value: Number(details[fieldName]),
                externalId: fieldName,
              };
            })
            .filter(Boolean);

          const savedSensitiveData = await saveBatchSensitiveRecruitingInfo(
            sensitiveData as BatchSensitiveRecruitingInfo[],
          );

          const encryptedValues = Object.fromEntries(
            savedSensitiveData.map((savedData: any) => {
              return [`${savedData.externalId}Id`, savedData.id];
            }),
          );

          return {
            baseAmountType: details.baseAmountType,
            currency: details.currency,
            office: details.office,
            contractType: details.contractType,
            ...encryptedValues,
          };
        }),
      );
      const requestData = { candidateId, compensationDetails };
      const response = await sendPostRequest(`${parentResourceUrl}${OFFER_URL}`, requestData);
      return response.json();
    } catch (error) {
      addMessage(new ErrorMessage('Error during creating offer'));
      throw error;
    }
  };

  const update = async (offerId: StringOrNumber, formData: FormFieldsData) => {
    try {
      const currentData = offerData;
      const compensationDetails = await prepareCompensationDetails(formData.compensationDetails, currentData);

      const requestData = {
        id: offerId,
        candidateId,
        compensationDetails,
      };

      await sendPutRequest(`${parentResourceUrl}${OFFER_URL}/${offerId}`, requestData);

      if (currentData?.offerDocumentPath) {
        await uploadOfferDocument({
          ...currentData,
          id: String(offerId),
          candidateId,
          compensationDetails: formData.compensationDetails,
        });
      }

      return getById(offerId);
    } catch (error) {
      addMessage(new ErrorMessage('Error during updating offer'));
      throw error;
    }
  };

  const deleteOffer = async (offerId: any) => {
    try {
      return await sendDeleteRequest(`${parentResourceUrl}${OFFER_URL}/${offerId}`);
    } catch (error) {
      addMessage(new ErrorMessage('Failed to delete offer'));
      throw error;
    }
  };

  const cancelOffer = async (offerId: any) => {
    try {
      const response = await sendPostRequest(`${parentResourceUrl}${OFFER_URL}/${offerId}/cancel`);
      return response.json();
    } catch (error) {
      addMessage(new ErrorMessage('Failed to cancel offer'));
      throw error;
    }
  };

  const acceptOffer = async (offerId: any) => {
    try {
      const response = await sendPostRequest(`${parentResourceUrl}${OFFER_URL}/${offerId}/accept`);
      return response.json();
    } catch (error) {
      addMessage(new ErrorMessage('Failed to accept offer'));
      throw error;
    }
  };

  const rejectOffer = async (offerId: any) => {
    try {
      const response = await sendPostRequest(`${parentResourceUrl}${OFFER_URL}/${offerId}/reject`);
      return response.json();
    } catch (error) {
      addMessage(new ErrorMessage('Failed to reject offer'));
      throw error;
    }
  };

  const sendOffer = async (offerId: StringOrNumber) => {
    try {
      const response = await sendPostRequest(`${parentResourceUrl}${OFFER_URL}/${offerId}/send`);
      return response.json();
    } catch (error: unknown) {
      const errorMessage = await getApiErrorMessage(error, 'Failed to send offer');

      addMessage(new ErrorMessage(errorMessage));
      throw new Error(errorMessage);
    }
  };

  const finalizeOffer = async (offerId: any) => {
    try {
      const response = await sendPostRequest(`${parentResourceUrl}${OFFER_URL}/${offerId}/finalize`);
      return response.json();
    } catch (error) {
      addMessage(new ErrorMessage('Failed to finalize offer'));
      throw error;
    }
  };

  const getOfferPermissions = async (offerId: any) => {
    try {
      const response = await sendGetRequest(`/permissions/Offer/${offerId}`);
      return response.json();
    } catch (error) {
      addMessage(new ErrorMessage('Failed to fetch permissions for offer.'));
      return [];
    }
  };

  const getValidationSchema = () => Promise.resolve(offerValidationSchema);

  const commitGeneratedOfferDocument = async (offerId: StringOrNumber, tempPath: string) => {
    if (!candidateId) return null;

    try {
      const response = await sendPostRequest(
        `${parentResourceUrl}${OFFER_URL}/${candidateId}/${offerId}/document/commit`,
        {
          paths: [tempPath],
        },
      );

      return response.json();
    } catch (error: unknown) {
      const errorMessage = await getApiErrorMessage(error, 'Failed to save offer document');

      throw new Error(errorMessage);
    }
  };

  return {
    ...baseCrudRequests,
    getValidationSchema,
    createOffer,
    update,
    commitGeneratedOfferDocument,
    getById,
    sendOffer,
    rejectOffer,
    acceptOffer,
    cancelOffer,
    deleteOffer,
    finalizeOffer,
    getOfferPermissions,
  };
};

export default useCandidateOfferService;
